import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  addNotification,
  removeNotification,
  clearAllNotifications,
  markAsRead,
  updateConfig,
  AddNotificationPayload,
  UpdateConfigPayload,
} from '@/store/slices/notificationsSlice';

/**
 * Custom hook for managing notifications
 * Provides methods for creating, removing, and managing notifications
 */
export const useNotifications = () => {
  const dispatch = useAppDispatch();
  const { notifications, history, config } = useAppSelector(state => state.notifications);

  // Add a notification
  const notify = useCallback(
    (payload: AddNotificationPayload) => {
      dispatch(
        addNotification({
          preventDuplicates: true, // Default to preventing duplicates
          ...payload,
        })
      );
    },
    [dispatch]
  );

  // Helper methods for common notification types
  const success = useCallback(
    (title: string, message: string, options?: Partial<AddNotificationPayload>) => {
      dispatch(
        addNotification({
          type: 'success',
          title,
          message,
          preventDuplicates: true,
          ...options,
        })
      );
    },
    [dispatch]
  );

  const error = useCallback(
    (title: string, message: string, options?: Partial<AddNotificationPayload>) => {
      dispatch(
        addNotification({
          type: 'error',
          title,
          message,
          autoDismiss: false, // Errors typically require user action
          preventDuplicates: true,
          ...options,
        })
      );
    },
    [dispatch]
  );

  const warning = useCallback(
    (title: string, message: string, options?: Partial<AddNotificationPayload>) => {
      dispatch(
        addNotification({
          type: 'warning',
          title,
          message,
          preventDuplicates: true,
          ...options,
        })
      );
    },
    [dispatch]
  );

  const info = useCallback(
    (title: string, message: string, options?: Partial<AddNotificationPayload>) => {
      dispatch(
        addNotification({
          type: 'info',
          title,
          message,
          preventDuplicates: true,
          ...options,
        })
      );
    },
    [dispatch]
  );

  // Remove a notification
  const remove = useCallback(
    (id: string) => {
      dispatch(removeNotification(id));
    },
    [dispatch]
  );

  // Clear all notifications
  const clearAll = useCallback(() => {
    dispatch(clearAllNotifications());
  }, [dispatch]);

  // Mark a notification as read
  const markNotificationAsRead = useCallback(
    (id: string) => {
      dispatch(markAsRead(id));
    },
    [dispatch]
  );

  // Update notification configuration
  const updateNotificationConfig = useCallback(
    (config: UpdateConfigPayload) => {
      dispatch(updateConfig(config));
    },
    [dispatch]
  );

  return {
    notifications,
    history,
    config,
    notify,
    success,
    error,
    warning,
    info,
    remove,
    clearAll,
    markAsRead: markNotificationAsRead,
    updateConfig: updateNotificationConfig,
  };
};
